<template>
  <div class="image-uploader">
    <PageSpinner :onloader="onloader" />
    <div
      class="dropzone-container"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <input
        type="file"
        name="file"
        :id="inputId"
        class="hidden-input"
        @change="onChange"
        ref="file"
        accept=".jpg,.jpeg,.png"
      />

      <label :for="inputId" class="file-label">
        <div v-if="isDragging">Release to drop image here.</div>
        <div v-else>Drop image here or <u>click here</u> to upload.</div>
      </label>

      <p class="upload-guidance">
        JPG or PNG · images are resized when needed (maximum 5 MB)
      </p>

      <div class="preview-container mt-4" v-if="files.length">
        <div
          v-for="(file, index) in files"
          :key="`${file.name}-${file.lastModified}`"
          class="preview-card"
        >
          <div class="preview-file">
            <img
              class="preview-img"
              :src="previewUrls[index]"
              :alt="`Preview of ${file.name}`"
            />
            <p :title="file.name">
              {{ makeName(file.name) }}
            </p>
            <small>{{ formatFileSize(file.size) }}</small>
          </div>
          <div class="preview-actions">
            <button
              class="m-2 btn btn-danger btn-sm"
              type="button"
              @click="remove(files.indexOf(file))"
              title="Remove image"
              :disabled="isUploading"
            >
              <b><i class="fa fa-times"></i> Remove</b>
            </button>

            <button
              class="m-2 btn btn-success btn-sm"
              type="button"
              @click="uploadFile(file)"
              title="Upload file"
              :disabled="isUploading"
            >
              <b
                ><i
                  :class="isUploading ? 'fa fa-spinner fa-spin' : 'fa fa-check'"
                ></i>
                {{ isUploading ? "Preparing image..." : "I'm OK with this" }}</b
              >
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import PageSpinner from "@/components/PageSpinner";
export default {
  mixins: [Alert],
  components: {
    PageSpinner,
  },
  data() {
    return {
      isDragging: false,
      ImgPathval: null,
      onloader: "none",
      files: [],
      previewUrls: [],
      isUploading: false,
    };
  },
  props: {
    existingPath: { type: String, default: "" },
  },
  watch: {
    existingPath: {
      immediate: true,
      handler(value) {
        this.ImgPathval = value || null;
        this.clearSelectedFiles();
      },
    },
  },
  computed: {
    // unique per instance so multiple uploaders on one page (e.g. inside modals) don't share a DOM id
    inputId() {
      return `fileInput-${this._uid}`;
    },
  },
  methods: {
    onChange(event) {
      const selectedFiles = event && event.target ? event.target.files : [];
      this.setSelectedFiles(selectedFiles);
      if (this.$refs.file) this.$refs.file.value = "";
    },
    setSelectedFiles(fileList) {
      const file = fileList && fileList.length ? fileList[0] : null;
      this.clearSelectedFiles();
      if (!file) return;

      const extension = file.name.split(".").pop().toLowerCase();
      const acceptedExtensions = ["jpg", "jpeg", "png"];
      const acceptedMimeTypes = ["image/jpeg", "image/png"];
      if (
        !acceptedExtensions.includes(extension) ||
        (file.type && !acceptedMimeTypes.includes(file.type.toLowerCase()))
      ) {
        this.ShowAlertError("Choose a JPG or PNG image.", "");
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        this.ShowAlertError(
          "This image is larger than 25 MB and cannot be processed. Choose a smaller JPG or PNG image.",
          ""
        );
        return;
      }

      this.files = [file];
      this.previewUrls = [URL.createObjectURL(file)];
    },
    async uploadFile(file) {
      if (this.isUploading) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        this.ShowAlertError(
          "You are offline. Reconnect to upload the image.",
          ""
        );
        return;
      }

      if (!file) {
        this.ShowAlertError("Select an image before uploading.", "");
        return;
      }

      this.isUploading = true;
      this.onloader = "flex";
      try {
        const uploadFile = await this.prepareImage(file);
        const form = new FormData();
        form.append("username", this.$store.state.auth.username);
        form.append("accesskey", this.$store.state.auth.accesskey);
        form.append("formalurl", this.ImgPathval || "");
        form.append("doctype", "image");
        form.append("file", uploadFile, uploadFile.name);

        const response = await this.$store.dispatch("uploadfile", form);
        if (
          response &&
          response.responseCode &&
          response.responseCode !== "00"
        ) {
          throw new Error(
            response.responseMessage || "The image could not be uploaded."
          );
        }

        const uploadedPath =
          typeof response === "string"
            ? response
            : response &&
              (response.filePath ||
                response.FilePath ||
                response.path ||
                response.url ||
                (typeof response.data === "string" && response.data));
        if (!uploadedPath || typeof uploadedPath !== "string") {
          throw new Error(
            "The server did not return the uploaded image path. Please try again."
          );
        }

        this.ImgPathval = uploadedPath;
        this.clearSelectedFiles();
        this.emitOutImgPathval();
        this.ShowAlertSuccess("Uploaded successfully", "");
      } catch (err) {
        const responseData = err && err.response && err.response.data;
        const message =
          (typeof responseData === "string" && responseData) ||
          (responseData &&
            (responseData.message || responseData.responseMessage)) ||
          (err && err.message) ||
          "The image could not be uploaded. Please try again.";
        this.ShowAlertError(message, "");
      } finally {
        this.onloader = "none";
        this.isUploading = false;
      }
    },
    async prepareImage(file) {
      const maxUploadBytes = 5 * 1024 * 1024;
      const maxDimension = 1600;
      const extension = file.name.split(".").pop().toLowerCase();
      const mimeType = extension === "png" ? "image/png" : "image/jpeg";
      const source = await this.loadImage(file);

      try {
        const largestDimension = Math.max(source.width, source.height);
        if (file.size <= maxUploadBytes && largestDimension <= maxDimension) {
          return file;
        }

        let scale = Math.min(1, maxDimension / largestDimension);
        let quality = 0.86;
        let blob = null;

        for (let attempt = 0; attempt < 8; attempt += 1) {
          const canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(source.width * scale));
          canvas.height = Math.max(1, Math.round(source.height * scale));
          const context = canvas.getContext("2d");
          if (!context)
            throw new Error(
              "This browser could not process the selected image."
            );

          if (mimeType === "image/jpeg") {
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, canvas.width, canvas.height);
          }
          context.drawImage(source.image, 0, 0, canvas.width, canvas.height);
          blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, mimeType, quality)
          );
          if (!blob)
            throw new Error(
              "The selected image could not be processed. Try another JPG or PNG."
            );
          if (blob.size <= maxUploadBytes) break;

          scale *= 0.82;
          if (mimeType === "image/jpeg")
            quality = Math.max(0.58, quality - 0.08);
        }

        if (!blob || blob.size > maxUploadBytes) {
          throw new Error(
            "This image could not be reduced below 5 MB. Choose a smaller image or save it as JPG and try again."
          );
        }

        const baseName = file.name.replace(/\.[^.]+$/, "");
        return new File(
          [blob],
          `${baseName}.${extension === "png" ? "png" : "jpg"}`,
          {
            type: mimeType,
            lastModified: Date.now(),
          }
        );
      } finally {
        if (source.cleanup) source.cleanup();
      }
    },
    async loadImage(file) {
      if (typeof createImageBitmap === "function") {
        try {
          const bitmap = await createImageBitmap(file);
          return {
            image: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            cleanup: () => bitmap.close(),
          };
        } catch (error) {
          // Fall back to the Image decoder for browsers that reject createImageBitmap.
        }
      }

      const objectUrl = URL.createObjectURL(file);
      try {
        const image = await new Promise((resolve, reject) => {
          const element = new Image();
          element.onload = () => resolve(element);
          element.onerror = () =>
            reject(new Error("The selected image could not be opened."));
          element.src = objectUrl;
        });
        return {
          image,
          width: image.naturalWidth,
          height: image.naturalHeight,
          cleanup: () => URL.revokeObjectURL(objectUrl),
        };
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
      }
    },
    emitOutImgPathval() {
      this.$emit("emitOutImgPathval", this.ImgPathval);
    },
    makeName(name) {
      return (
        name.split(".")[0].substring(0, 3) +
        "..." +
        name.split(".")[name.split(".").length - 1]
      );
    },

    remove(i) {
      this.files.splice(i, 1);
      const previewUrl = this.previewUrls.splice(i, 1)[0];
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },

    clearSelectedFiles() {
      this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
      this.previewUrls = [];
      this.files = [];
    },

    formatFileSize(size) {
      if (!size) return "0 B";
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },

    dragover(e) {
      e.preventDefault();
      this.isDragging = true;
    },

    dragleave() {
      this.isDragging = false;
    },

    drop(e) {
      e.preventDefault();
      this.setSelectedFiles(e.dataTransfer.files);
      this.isDragging = false;
    },
  },
  beforeDestroy() {
    this.clearSelectedFiles();
  },
};
</script>

<style scoped>
.image-uploader {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  text-align: center;
  min-width: 0;
}

.dropzone-container {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 1rem;
  border-radius: 0.75rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
}

.hidden-input {
  opacity: 0;
  overflow: hidden;
  position: absolute;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.file-label {
  width: 100%;
  max-width: 100%;
  margin: 0;
  color: #526273;
  font-size: 0.95rem;
  font-weight: 600;
  display: block;
  cursor: pointer;
  overflow-wrap: anywhere;
  word-break: normal;
}

.upload-guidance {
  max-width: 100%;
  margin: 0.55rem 0 0;
  color: #83909e;
  font-size: 0.72rem;
  overflow-wrap: anywhere;
}

.preview-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 1rem;
}

.preview-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  max-width: 440px;
  padding: 0.75rem;
  border: 1px solid #dce4eb;
  border-radius: 0.75rem;
  background: #fff;
  text-align: left;
}

.preview-file {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  column-gap: 0.65rem;
  min-width: 0;
}

.preview-img {
  grid-row: span 2;
  width: 56px;
  height: 56px;
  border-radius: 0.55rem;
  border: 1px solid #e1e6eb;
  background: #f1f3f5;
  object-fit: cover;
}

.preview-file p {
  min-width: 0;
  margin: 0;
  color: #34465a;
  font-size: 0.8rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-file small {
  color: #8491a0;
  font-size: 0.68rem;
}

.preview-actions {
  display: grid;
  gap: 0.4rem;
}

.preview-actions .btn {
  margin: 0 !important;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .dropzone-container {
    padding: 1rem 0.75rem;
  }

  .preview-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .preview-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .preview-actions .btn {
    padding: 0.45rem 0.5rem;
    font-size: 0.72rem;
  }
}
</style>
